import React, { useMemo, useState } from "react";
import axios from "axios";
import { Loader2, Zap, Leaf, CheckCircle2 } from "lucide-react";

// Earliest business day the customer can pick — mirrors the public
// BusinessDayPicker's `earliestSelectableDate` rule but rendered as a
// helper hint under the text input (the widget is too narrow for a
// month grid). Skips Sundays + US federal holidays + 6:30 PM cutoff.
const HOLIDAYS_2026_27 = new Set([
  "2026-01-01","2026-01-19","2026-02-16","2026-05-25","2026-06-19",
  "2026-07-03","2026-07-04","2026-09-07","2026-10-12","2026-11-11",
  "2026-11-26","2026-11-27","2026-12-24","2026-12-25","2026-12-31",
  "2027-01-01","2027-01-18","2027-02-15","2027-05-31","2027-06-19",
  "2027-07-05","2027-09-06","2027-10-11","2027-11-11","2027-11-25",
  "2027-11-26","2027-12-24","2027-12-25","2027-12-31",
]);
const fmtIso = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
const isBusinessDay = (d) => d.getDay() !== 0 && !HOLIDAYS_2026_27.has(fmtIso(d));
function nextBusinessDay() {
  const now = new Date();
  let cursor = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  // After-hours cutoff (6:30 PM weekdays, 2 PM Sat) → already roll to tomorrow
  const minutes = now.getHours() * 60 + now.getMinutes();
  const dow = now.getDay();
  const cutoffs = { 1: 1110, 2: 1110, 3: 1110, 4: 1110, 5: 1110, 6: 840 };
  if (!cutoffs[dow] || minutes >= cutoffs[dow]) {
    cursor.setDate(cursor.getDate() + 1);
  }
  while (!isBusinessDay(cursor)) cursor.setDate(cursor.getDate() + 1);
  return cursor;
}

/**
 * Customer-facing interactive cards rendered inside ChatWidget for the
 * in-chat PUCT §25.474 Third-Party Verification (TPV) flow. Each card
 * matches a `kind` returned by the backend on a chat message:
 *
 *   plan_cards          → PlanCardCustomer       (one card per eligible plan)
 *   disclosure_card     → DisclosureCardCustomer (YRAC + EFL + TOS)
 *   terms_card          → TermsCardCustomer      (typed-name e-signature)
 *   otp_card            → OtpCardCustomer        (Twilio Verify code entry)
 *   payment_card        → PaymentCardCustomer    (Stripe checkout link)
 *   enrollment_complete → CompleteCardCustomer   (success summary)
 *
 * Extracted from ChatWidget.jsx (Feb 2026) to keep that file under 850
 * lines and make individual cards easier to find and tweak.
 */

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "") + "/api";

// =====================================================================
// CustomerDataCard — interactive form rendered when the system sends a
// `customer_data_card` message. Mirrors the public /enroll "Tus Datos"
// step exactly: name, email, phone, DOB, service start date.
//
// Validations happen server-side (POST /enrollment/submit-customer-data
// runs the same checks as /validate/email and /validate/phone). On
// errors the backend returns { ok:false, errors: {fieldKey: message} }
// and we render them inline per-field.
//
// On success the backend auto-advances the enrollment to the
// disclosures card, so this component just disables itself.
// =====================================================================
export function CustomerDataCard({ data, lang, sessionId, onSubmitted }) {
  const [form, setForm] = useState({
    nombre: data?.nombre || "",
    apellido: data?.apellido || "",
    email: data?.email || "",
    telefono: data?.telefono || "",
    fecha_nacimiento: data?.fecha_nacimiento || "",
    fecha_inicio: data?.fecha_inicio || "",
    phone_is_contact: data?.phone_is_contact !== false,
  });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async () => {
    if (busy || done) return;
    setBusy(true); setErrors({});
    try {
      const { data: res } = await axios.post(`${API}/chat/${sessionId}/enrollment/submit-customer-data`, form);
      if (res?.ok) { setDone(true); onSubmitted?.(); }
      else { setErrors(res?.errors || { _: "No pudimos guardar tus datos" }); }
    } catch (e) {
      const det = e?.response?.data;
      if (det?.errors) setErrors(det.errors);
      else setErrors({ _: det?.detail || "Error al enviar" });
    } finally { setBusy(false); }
  };

  const L = (es, en) => (lang === "en" ? en : es);

  return (
    <div className="bg-white border-2 border-velcor-green/40 rounded-2xl p-3 shadow-sm space-y-2.5" data-testid="customer-data-card">
      <div className="text-[11px] font-extrabold uppercase tracking-wider text-velcor-green">
        {L("Tus datos", "Your details")}
      </div>

      {/* Name */}
      <div className="grid grid-cols-2 gap-2">
        <CdField label={L("Nombre", "First name")} value={form.nombre} error={errors.nombre} testid="cd-nombre"
          onChange={(v) => set("nombre", v)} disabled={done} />
        <CdField label={L("Apellido", "Last name")} value={form.apellido} error={errors.apellido} testid="cd-apellido"
          onChange={(v) => set("apellido", v)} disabled={done} />
      </div>

      {/* Email */}
      <CdField label={L("Correo", "Email")} type="email" value={form.email} error={errors.email} testid="cd-email"
        onChange={(v) => set("email", v)} disabled={done} />

      {/* Phone */}
      <CdField label={L("Móvil (10 dígitos)", "Mobile (10 digits)")} type="tel"
        value={form.telefono} error={errors.telefono} testid="cd-telefono"
        onChange={(v) => set("telefono", v.replace(/\D/g, "").slice(0, 10))}
        disabled={done} />

      {/* DOB */}
      <CdField label={L("Fecha de nacimiento", "Date of birth")} placeholder="MM/DD/AAAA"
        value={form.fecha_nacimiento} error={errors.fecha_nacimiento} testid="cd-dob"
        onChange={(v) => set("fecha_nacimiento", v)} disabled={done} />

      {/* Service start — typed MM/DD/YYYY (widget is too narrow for a
          calendar grid). We auto-format slashes as the customer types
          and show a hint with the next business day so they don't pick
          a Sunday or holiday by accident. Backend validates server-side. */}
      <FechaInicioField
        value={form.fecha_inicio}
        error={errors.fecha_inicio}
        onChange={(v) => set("fecha_inicio", v)}
        disabled={done}
        lang={lang}
      />

      {errors._ && <div className="text-rose-600 text-[11px] font-bold">{errors._}</div>}

      <button onClick={submit} disabled={busy || done}
        className={`w-full h-9 rounded-lg font-extrabold text-[12px] inline-flex items-center justify-center gap-1.5 transition-colors ${
          done ? "bg-emerald-50 text-emerald-700 border-2 border-emerald-300"
               : "bg-velcor-green text-white hover:bg-velcor-green-dark disabled:opacity-50"
        }`}
        data-testid="customer-data-submit"
      >
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : done ? <CheckCircle2 className="w-3.5 h-3.5" /> : null}
        {done ? L("Datos guardados", "Saved") : busy ? L("Enviando…", "Submitting…") : L("Continuar", "Continue")}
      </button>
    </div>
  );
}

// Small reusable field with inline error display. Kept private to this
// file to avoid leaking another tiny component into the public API.
function CdField({ label, value, error, testid, onChange, type = "text", placeholder = "", disabled }) {
  return (
    <div>
      <label className="block text-[10.5px] font-extrabold uppercase tracking-wider text-slate-500 mb-1">{label}</label>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} disabled={disabled}
        className={`w-full h-9 px-2 rounded-lg border bg-white text-velcor-ink text-[12.5px] focus:outline-none disabled:bg-slate-50 disabled:text-slate-500 ${
          error ? "border-rose-400 focus:border-rose-500" : "border-slate-300 focus:border-velcor-green"
        }`}
        data-testid={testid}
      />
      {error && <div className="text-rose-600 text-[10.5px] font-bold mt-0.5">{error}</div>}
    </div>
  );
}

// =====================================================================
// PlanCardCustomer — interactive card rendered when an agent sends the
// customer a `plan_cards` message. Customer taps "Elegir este plan" →
// POST /chat/{id}/enrollment/select-plan → the chat auto-refreshes and
// the customer sees an agent confirmation.
//
// Idempotent: once selected, subsequent taps on any card are ignored
// (the backend will 400 anyway because status moved past `plan_sent`).
// =====================================================================
export function PlanCardCustomer({ plan, lang, sessionId, onSelected }) {
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const pick = async () => {
    if (!sessionId || busy || done) return;
    setBusy(true);
    try {
      await axios.post(`${API}/chat/${sessionId}/enrollment/select-plan`, { plan_id: plan.plan_id });
      setDone(true);
      onSelected?.();
    } catch (e) {
      const det = e?.response?.data?.detail;
      // eslint-disable-next-line no-alert
      alert(typeof det === "string" ? det : (lang === "en" ? "Could not select plan" : "No pudimos seleccionar el plan"));
    } finally {
      setBusy(false);
    }
  };
  const rateStr = (plan.rate_cents_per_kwh || 0).toFixed(1);
  const monthly = plan.estimated_monthly;
  const usage = plan.usage_anchor;
  return (
    <div className="bg-white border-2 border-velcor-green/40 rounded-2xl p-3 shadow-sm" data-testid={`plan-card-${plan.plan_id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="font-extrabold text-velcor-ink text-[14px] leading-tight">{plan.name}</div>
          <div className="text-[11px] text-slate-500 mt-0.5">{plan.term}</div>
        </div>
        <div className="text-right shrink-0">
          <div className="text-velcor-green font-extrabold text-[16px] inline-flex items-center gap-0.5">
            <Zap className="w-3 h-3" strokeWidth={2.6} />{rateStr}¢
          </div>
          <div className="text-[9.5px] text-slate-400 -mt-0.5">/kWh</div>
        </div>
      </div>
      {monthly > 0 && (
        <div className="mt-2 rounded-lg bg-velcor-green/5 border border-velcor-green/15 px-2.5 py-1.5 flex items-baseline justify-between"
             data-testid={`plan-card-estimate-${plan.plan_id}`}>
          <span className="text-[10px] font-extrabold uppercase tracking-wider text-velcor-green/80">
            {lang === "en" ? "Est. monthly" : "Est. mensual"}
          </span>
          <span className="text-velcor-ink font-extrabold text-[15px] tabular-nums">
            ${Number(monthly).toLocaleString()}
            {usage > 0 && (
              <span className="text-[10px] font-bold text-slate-400 ml-1">
                @ {Number(usage).toLocaleString()} kWh
              </span>
            )}
          </span>
        </div>
      )}
      <div className="flex items-center gap-2.5 mt-2 text-[10.5px] text-slate-600">
        {plan.renewable_pct > 0 && (
          <span className="inline-flex items-center gap-0.5 text-emerald-600 font-bold">
            <Leaf className="w-2.5 h-2.5" />{plan.renewable_pct}% {lang === "en" ? "renewable" : "renovable"}
          </span>
        )}
        {plan.cancellation_fee > 0 && (
          <span className="text-slate-500">ETF ${plan.cancellation_fee}</span>
        )}
        {plan.base_charge > 0 && (
          <span className="text-slate-500">{lang === "en" ? "Base" : "Cargo base"} ${plan.base_charge}</span>
        )}
      </div>
      <button onClick={pick} disabled={busy || done}
        className={`mt-3 w-full h-9 rounded-lg font-extrabold text-[12px] inline-flex items-center justify-center gap-1.5 transition-colors ${
          done ? "bg-emerald-50 text-emerald-700 border-2 border-emerald-300"
               : "bg-velcor-green text-white hover:bg-velcor-green-dark disabled:opacity-60"
        }`}
        data-testid={`plan-card-pick-${plan.plan_id}`}
      >
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : done ? <CheckCircle2 className="w-3.5 h-3.5" /> : null}
        {done
          ? (lang === "en" ? "Selected" : "Plan elegido")
          : busy
            ? (lang === "en" ? "Selecting…" : "Eligiendo…")
            : (lang === "en" ? "Choose this plan" : "Elegir este plan")}
      </button>
    </div>
  );
}

export function DisclosureCardCustomer({ data, lang, sessionId, onAcked }) {
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const ack = async () => {
    if (busy || done || !agreed) return;
    setBusy(true);
    try {
      await axios.post(`${API}/chat/${sessionId}/enrollment/ack-disclosures`);
      setDone(true); onAcked?.();
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert(e?.response?.data?.detail || (lang === "en" ? "Could not confirm" : "No pudimos confirmar"));
    } finally { setBusy(false); }
  };
  const docs = [
    { key: "yrac", label: lang === "en" ? "Your Rights as a Customer" : "Tus Derechos como Cliente (YRAC)", url: data.yrac_url },
    { key: "efl",  label: lang === "en" ? "Electricity Facts Label (EFL)" : "Etiqueta de Datos (EFL)", url: data.efl_url },
    { key: "tos",  label: lang === "en" ? "Terms of Service" : "Términos de Servicio (TOS)", url: data.tos_url },
  ];
  return (
    <div className="bg-white border-2 border-velcor-green/40 rounded-2xl p-3 shadow-sm space-y-2" data-testid="disclosure-card">
      <div className="text-[11px] font-extrabold uppercase tracking-wider text-velcor-green">
        {lang === "en" ? "Required disclosures" : "Documentos requeridos"}
      </div>
      {/* Document links — optional to open. Customer can review if they
          want, but the single acknowledgement checkbox below is what
          PUCT §25.474(f) requires (acknowledge receipt, not 'must read'). */}
      {docs.map((row) => (
        <a key={row.key} href={row.url} target="_blank" rel="noopener noreferrer"
          className="block px-3 py-1.5 rounded-lg border border-slate-200 bg-white hover:border-slate-300 text-[12px] font-bold text-velcor-ink"
          data-testid={`disclosure-${row.key}`}
        >
          {row.label} <span className="text-[10px] text-slate-400 font-normal">({lang === "en" ? "view" : "ver"} ↗)</span>
        </a>
      ))}
      {/* Single confirmation checkbox — much less friction than forcing
          customers to open each link before continuing. */}
      <label className={`flex items-start gap-2 mt-2 px-2.5 py-2 rounded-lg border-2 cursor-pointer select-none ${
        agreed || done ? "bg-emerald-50 border-emerald-300" : "bg-slate-50 border-slate-200 hover:border-slate-300"
      }`} data-testid="disclosure-agree-toggle">
        <input type="checkbox" checked={agreed || done} disabled={done}
          onChange={(e) => setAgreed(e.target.checked)}
          className="mt-0.5 w-4 h-4 accent-velcor-green cursor-pointer"
        />
        <span className="text-[12px] text-velcor-ink leading-snug">
          {lang === "en"
            ? "I confirm I received and understand the 3 documents above."
            : "Confirmo que recibí y entiendo los 3 documentos anteriores."}
        </span>
      </label>
      <button onClick={ack} disabled={busy || done || !agreed}
        className={`w-full h-9 rounded-lg font-extrabold text-[12px] inline-flex items-center justify-center gap-1.5 transition-colors ${
          done ? "bg-emerald-50 text-emerald-700 border-2 border-emerald-300"
               : "bg-velcor-green text-white hover:bg-velcor-green-dark disabled:opacity-40"
        }`}
        data-testid="disclosure-ack-btn"
      >
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : done ? <CheckCircle2 className="w-3.5 h-3.5" /> : null}
        {done
          ? (lang === "en" ? "Confirmed" : "Confirmado")
          : !agreed
            ? (lang === "en" ? "Check the box to continue" : "Marca la casilla para continuar")
            : (lang === "en" ? "Continue" : "Continuar")}
      </button>
    </div>
  );
}

export function TermsCardCustomer({ data, lang, sessionId, onSigned }) {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const sign = async () => {
    if (!name.trim() || busy || done) return;
    setBusy(true);
    try {
      await axios.post(`${API}/chat/${sessionId}/enrollment/sign-terms`, { typed_name: name.trim() });
      setDone(true); onSigned?.();
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert(e?.response?.data?.detail || "Error");
    } finally { setBusy(false); }
  };
  return (
    <div className="bg-white border-2 border-velcor-green/40 rounded-2xl p-3 shadow-sm space-y-2" data-testid="terms-card">
      <div className="text-[11px] font-extrabold uppercase tracking-wider text-velcor-green">
        {lang === "en" ? "Sign to authorize" : "Firma para autorizar"}
      </div>
      <div className="bg-slate-50 rounded-lg p-2.5 text-[11.5px] text-velcor-ink leading-relaxed border border-slate-200">
        <div><strong>{lang === "en" ? "Plan" : "Plan"}:</strong> {data.plan_name}</div>
        <div><strong>{lang === "en" ? "Rate" : "Tarifa"}:</strong> {(data.rate_cents_per_kwh || 0).toFixed(1)}¢/kWh · {data.term}</div>
        <div><strong>{lang === "en" ? "Cancellation fee" : "Cargo por cancelación"}:</strong> ${data.cancellation_fee || 0}</div>
        <div><strong>{lang === "en" ? "Service address" : "Dirección de servicio"}:</strong> {data.address}</div>
      </div>
      {!done && (
        <>
          <label className="block text-[10.5px] font-extrabold uppercase tracking-wider text-slate-500">
            {lang === "en" ? "Type your full legal name to sign" : "Escribe tu nombre legal completo para firmar"}
          </label>
          <input type="text" value={name} onChange={(e) => setName(e.target.value)}
            placeholder={data.customer_name || (lang === "en" ? "Full name" : "Nombre completo")}
            className="w-full h-9 px-3 rounded-lg border border-slate-300 text-velcor-ink text-[13px] focus:border-velcor-green focus:outline-none"
            data-testid="terms-sign-input"
          />
        </>
      )}
      <button onClick={sign} disabled={busy || done || !name.trim()}
        className={`w-full h-9 rounded-lg font-extrabold text-[12px] inline-flex items-center justify-center gap-1.5 transition-colors ${
          done ? "bg-emerald-50 text-emerald-700 border-2 border-emerald-300"
               : "bg-velcor-green text-white hover:bg-velcor-green-dark disabled:opacity-40"
        }`}
        data-testid="terms-sign-btn"
      >
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : done ? <CheckCircle2 className="w-3.5 h-3.5" /> : null}
        {done
          ? (lang === "en" ? "Signed" : "Firmado")
          : (lang === "en" ? "I accept and authorize the switch" : "Acepto y autorizo el cambio")}
      </button>
    </div>
  );
}

export function OtpCardCustomer({ data, lang, sessionId, onVerified }) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");
  const verify = async () => {
    if (!code.trim() || busy || done) return;
    setBusy(true); setErr("");
    try {
      await axios.post(`${API}/chat/${sessionId}/enrollment/verify-otp`, { code: code.trim() });
      setDone(true); onVerified?.();
    } catch (e) {
      setErr(e?.response?.data?.detail || (lang === "en" ? "Wrong code" : "Código incorrecto"));
    } finally { setBusy(false); }
  };
  return (
    <div className="bg-white border-2 border-velcor-green/40 rounded-2xl p-3 shadow-sm space-y-2" data-testid="otp-card">
      <div className="text-[11px] font-extrabold uppercase tracking-wider text-velcor-green">
        {lang === "en" ? "Verify your phone" : "Verifica tu teléfono"}
      </div>
      <div className="text-[12px] text-slate-600">
        {lang === "en" ? "Code sent to" : "Código enviado a"} <strong className="text-velcor-ink">{data.phone_masked}</strong>
      </div>
      {!done && (
        <input type="tel" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 8))}
          placeholder="123456" maxLength={8}
          className="w-full h-10 px-3 rounded-lg border border-slate-300 text-velcor-ink text-[16px] font-mono tracking-widest text-center focus:border-velcor-green focus:outline-none"
          data-testid="otp-input"
        />
      )}
      {err && <div className="text-rose-600 text-[11.5px] font-bold">{err}</div>}
      <button onClick={verify} disabled={busy || done || code.length < 4}
        className={`w-full h-9 rounded-lg font-extrabold text-[12px] inline-flex items-center justify-center gap-1.5 transition-colors ${
          done ? "bg-emerald-50 text-emerald-700 border-2 border-emerald-300"
               : "bg-velcor-green text-white hover:bg-velcor-green-dark disabled:opacity-40"
        }`}
        data-testid="otp-verify-btn"
      >
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : done ? <CheckCircle2 className="w-3.5 h-3.5" /> : null}
        {done
          ? (lang === "en" ? "Phone verified" : "Teléfono verificado")
          : (lang === "en" ? "Verify code" : "Verificar código")}
      </button>
    </div>
  );
}

export function PaymentCardCustomer({ data, lang }) {
  return (
    <div className="bg-white border-2 border-velcor-green/40 rounded-2xl p-3 shadow-sm space-y-2" data-testid="payment-card">
      <div className="text-[11px] font-extrabold uppercase tracking-wider text-velcor-green">
        {lang === "en" ? "Refundable deposit" : "Depósito reembolsable"}
      </div>
      <div className="flex items-baseline gap-2">
        <span className="text-3xl font-extrabold text-velcor-ink">${data.amount}</span>
        <span className="text-[11px] text-slate-500">{lang === "en" ? "one-time" : "único pago"}</span>
      </div>
      <p className="text-[11.5px] text-slate-600 leading-snug">
        {lang === "en"
          ? "Refunded after 12 on-time months (PUCT §25.478). Secure card payment via Stripe."
          : "Reembolsado tras 12 meses puntuales (PUCT §25.478). Pago seguro con Stripe."}
      </p>
      <a href={data.checkout_url} target="_blank" rel="noopener noreferrer"
        className="w-full h-10 rounded-lg bg-velcor-green text-white font-extrabold text-[13px] inline-flex items-center justify-center gap-2 hover:bg-velcor-green-dark"
        data-testid="payment-stripe-link"
      >
        {lang === "en" ? `Pay $${data.amount} securely →` : `Pagar $${data.amount} con tarjeta →`}
      </a>
    </div>
  );
}

export function CompleteCardCustomer({ data, lang }) {
  return (
    <div className="bg-emerald-50 border-2 border-emerald-300 rounded-2xl p-4 shadow-sm space-y-1.5" data-testid="complete-card">
      <div className="flex items-center gap-2">
        <CheckCircle2 className="w-5 h-5 text-emerald-600" />
        <div className="font-extrabold text-emerald-900 text-[14px]">
          {lang === "en" ? "Enrollment complete!" : "¡Enrolamiento completado!"}
        </div>
      </div>
      <div className="text-[12px] text-emerald-800">
        {lang === "en" ? "Order" : "Orden"}: <strong className="font-mono">{data.order_number}</strong>
      </div>
      <div className="text-[12px] text-emerald-800">
        {lang === "en" ? "Plan" : "Plan"}: <strong>{data.plan_name}</strong>
      </div>
      <div className="text-[11px] text-emerald-700 mt-2">
        {lang === "en"
          ? "You'll receive a confirmation email with your full conversation transcript."
          : "Recibirás un email con la transcripción completa de la conversación."}
      </div>
    </div>
  );
}


/** Service-start text field with auto-slash formatting + hint of the next
 *  business day. Inline because it's used only by CustomerDataCard and
 *  needs the chat-local color tokens. */
function FechaInicioField({ value, error, onChange, disabled, lang }) {
  const L = (es, en) => (lang === "en" ? en : es);

  // Auto-format MM/DD/YYYY as user types — paste-friendly.
  const handle = (raw) => {
    const digits = (raw || "").replace(/\D/g, "").slice(0, 8);
    let out = digits;
    if (digits.length >= 5)      out = `${digits.slice(0,2)}/${digits.slice(2,4)}/${digits.slice(4)}`;
    else if (digits.length >= 3) out = `${digits.slice(0,2)}/${digits.slice(2)}`;
    onChange(out);
  };

  // Compute placeholder + helper from the next business day. Wrapped in
  // useMemo so we don't recompute on every keystroke.
  const next = useMemo(() => nextBusinessDay(), []);
  const nextMmDdYyyy = `${String(next.getMonth()+1).padStart(2,"0")}/${String(next.getDate()).padStart(2,"0")}/${next.getFullYear()}`;
  const dowName = lang === "en"
    ? ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"][next.getDay()]
    : ["Dom","Lun","Mar","Mié","Jue","Vie","Sáb"][next.getDay()];

  return (
    <div data-testid="cd-fecha-inicio-wrap">
      <label className="block text-[10.5px] font-extrabold uppercase tracking-wider text-slate-500 mb-1">
        {L("Inicio del servicio", "Service start")}
      </label>
      <input
        type="text"
        inputMode="numeric"
        value={value || ""}
        onChange={(e) => handle(e.target.value)}
        disabled={disabled}
        placeholder={nextMmDdYyyy}
        maxLength={10}
        className="w-full h-9 px-2 rounded-lg border border-slate-300 bg-white text-velcor-ink text-[12.5px] tabular-nums focus:border-velcor-green focus:outline-none disabled:bg-slate-50"
        data-testid="cd-fecha-inicio"
      />
      <div className="text-[10px] text-slate-500 mt-1" data-testid="cd-fecha-inicio-hint">
        {L(
          `Formato: MM/DD/AAAA · Sugerido: ${dowName} ${nextMmDdYyyy} (próximo día hábil)`,
          `Format: MM/DD/YYYY · Suggested: ${dowName} ${nextMmDdYyyy} (next business day)`
        )}
      </div>
      {error && (
        <div className="text-rose-600 text-[10.5px] font-bold mt-0.5" data-testid="cd-fecha-inicio-error">
          {error}
        </div>
      )}
    </div>
  );
}
